import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Bell, ArrowRight, ArrowLeft, MessageSquare, Image as ImageIcon, Zap, Snowflake, Droplet, Sparkles, Wifi, Shield, Sofa, MoreHorizontal } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { operacionesApi } from '../../lib/api';
import { cn } from '../../lib/utils';
import { useEdificioOp } from '../../contexts/EdificioOpContext';

const CAT_ICON = {
  electricidad: Zap, climatizacion: Snowflake, plomeria: Droplet,
  limpieza: Sparkles, internet: Wifi, seguridad: Shield, mobiliario: Sofa, otros: MoreHorizontal,
};

const COLUMNS = [
  { estado: 'recibido', titulo: 'Recibidas', cls: 'bg-amber-50 border-amber-300', dot: 'bg-amber-500' },
  { estado: 'en_proceso', titulo: 'En proceso', cls: 'bg-blue-50 border-blue-300', dot: 'bg-blue-500' },
  { estado: 'resuelto', titulo: 'Resueltas', cls: 'bg-emerald-50 border-emerald-300', dot: 'bg-emerald-500' },
];

export default function OperacionesIncidenciasKanban() {
  const { edificioId, edificioActivo } = useEdificioOp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moving, setMoving] = useState(null); // { incidencia, nuevoEstado }
  const [nota, setNota] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  const reload = useCallback(async () => {
    if (!edificioId) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await operacionesApi.listIncidencias({ edificio_id: edificioId });
      setItems(r.data || []);
    } catch { toast.error('Error al cargar'); }
    finally { setLoading(false); }
  }, [edificioId]);

  useEffect(() => { reload(); }, [reload]);

  const filtered = items.filter(i => {
    if (!busqueda) return true;
    const t = busqueda.toLowerCase();
    return (i.descripcion || '').toLowerCase().includes(t) ||
      (i.reporter_nombre || '').toLowerCase().includes(t) ||
      (i.categoria || '').includes(t);
  });

  const itemsByEstado = COLUMNS.reduce((acc, c) => {
    acc[c.estado] = filtered.filter(i => i.estado === c.estado);
    return acc;
  }, {});

  const cambiar = async () => {
    if (!moving) return;
    try {
      await operacionesApi.cambiarEstadoIncidencia(moving.incidencia.incidencia_id, {
        estado: moving.nuevoEstado, nota,
      });
      toast.success('Estado actualizado');
      setMoving(null);
      setNota('');
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const moverEstado = (inc, dir) => {
    const idx = COLUMNS.findIndex(c => c.estado === inc.estado);
    const next = COLUMNS[idx + dir];
    if (!next) return;
    setMoving({ incidencia: inc, nuevoEstado: next.estado });
    setNota('');
  };

  // ---- Drag & drop handlers ----
  const handleDragStart = (e, inc) => {
    setDraggingId(inc.incidencia_id);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', inc.incidencia_id); } catch { /* IE */ }
  };
  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverCol(null);
  };
  const handleColDragOver = (e, estado) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverCol !== estado) setDragOverCol(estado);
  };
  const handleColDragLeave = (estado) => {
    if (dragOverCol === estado) setDragOverCol(null);
  };
  const handleColDrop = (e, estado) => {
    e.preventDefault();
    setDragOverCol(null);
    const id = draggingId || e.dataTransfer.getData('text/plain');
    setDraggingId(null);
    const inc = items.find(x => x.incidencia_id === id);
    if (!inc || inc.estado === estado) return;
    setMoving({ incidencia: inc, nuevoEstado: estado });
    setNota('');
  };

  if (loading) return <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 inline" /></div>;

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center">
            <Bell className="w-5 h-5 text-rose-700" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Reclamos · {edificioActivo?.nombre || ''}</h1>
            <p className="text-xs text-slate-500">{filtered.length} reportes · arrastrá las cards entre columnas o usá las flechas.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input placeholder="Buscar..." value={busqueda} onChange={e => setBusqueda(e.target.value)} className="h-9 w-44" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {COLUMNS.map(col => (
          <div key={col.estado}
            onDragOver={(e) => handleColDragOver(e, col.estado)}
            onDragLeave={() => handleColDragLeave(col.estado)}
            onDrop={(e) => handleColDrop(e, col.estado)}
            className={cn(
              'rounded-lg border p-3 flex flex-col min-h-[60vh] transition-all',
              col.cls,
              dragOverCol === col.estado && 'ring-2 ring-offset-1 ring-slate-900/30 scale-[1.01]'
            )}
            data-testid={`col-${col.estado}`}>
            <div className="flex items-center gap-2 mb-3">
              <span className={cn('w-2 h-2 rounded-full', col.dot)} />
              <h3 className="font-semibold text-slate-900 text-sm flex-1">{col.titulo}</h3>
              <span className="text-xs text-slate-500">{itemsByEstado[col.estado]?.length || 0}</span>
            </div>
            <div className="space-y-2 flex-1">
              {itemsByEstado[col.estado]?.length === 0 && (
                <div className="text-xs text-slate-400 text-center py-6 border-2 border-dashed border-slate-200 rounded">
                  {dragOverCol === col.estado ? 'Soltá acá' : '—'}
                </div>
              )}
              {itemsByEstado[col.estado]?.map(inc => (
                <KanbanCard key={inc.incidencia_id} inc={inc}
                  draggable
                  isDragging={draggingId === inc.incidencia_id}
                  onDragStart={(e) => handleDragStart(e, inc)}
                  onDragEnd={handleDragEnd}
                  canMoveLeft={COLUMNS.findIndex(c => c.estado === inc.estado) > 0}
                  canMoveRight={COLUMNS.findIndex(c => c.estado === inc.estado) < COLUMNS.length - 1}
                  onLeft={() => moverEstado(inc, -1)}
                  onRight={() => moverEstado(inc, 1)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!moving} onOpenChange={o => !o && setMoving(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cambiar a "{COLUMNS.find(c => c.estado === moving?.nuevoEstado)?.titulo}"</DialogTitle></DialogHeader>
          <div className="py-2 text-sm">
            {moving?.incidencia && (
              <div className="text-slate-700 mb-3 italic">"{moving.incidencia.descripcion}"</div>
            )}
            <Textarea rows={2} placeholder="Nota (opcional, queda en el historial)" value={nota} onChange={e => setNota(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoving(null)}>Cancelar</Button>
            <Button onClick={cambiar} data-testid="confirm-mover">Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const KanbanCard = ({ inc, canMoveLeft, canMoveRight, onLeft, onRight, draggable, isDragging, onDragStart, onDragEnd }) => {
  const Icon = CAT_ICON[inc.categoria] || MoreHorizontal;
  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={cn(
        'bg-white border border-slate-200 rounded p-2.5 shadow-sm hover:shadow transition cursor-grab active:cursor-grabbing select-none',
        isDragging && 'opacity-40 rotate-1'
      )}
      data-testid={`inc-${inc.incidencia_id}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5 text-slate-500" />
        <span className="text-[11px] font-medium uppercase text-slate-600 flex-1 truncate">{inc.categoria}</span>
        <span className="text-[10px] text-slate-400">{new Date(inc.created_at).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })}</span>
      </div>
      <p className="text-sm text-slate-800 leading-snug line-clamp-3">{inc.descripcion}</p>
      <div className="text-[11px] text-slate-500 mt-1.5 flex items-center justify-between">
        <span className="truncate flex-1">{inc.reporter_nombre}</span>
        {inc.foto_data_url && (
          <a href={inc.foto_data_url} target="_blank" rel="noreferrer"
             onClick={(e) => e.stopPropagation()}
             draggable={false}
             className="text-indigo-600 hover:underline ml-1 flex items-center gap-0.5">
            <ImageIcon className="w-3 h-3" /> foto
          </a>
        )}
      </div>
      {inc.historial?.length > 1 && (
        <div className="mt-1 flex items-center gap-1 text-[10px] text-slate-400">
          <MessageSquare className="w-3 h-3" /> {inc.historial.length} actualizaciones
        </div>
      )}
      <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-slate-100">
        <button onClick={onLeft} disabled={!canMoveLeft}
          className="text-[11px] text-slate-500 hover:text-slate-800 disabled:opacity-25 disabled:cursor-not-allowed flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Atrás
        </button>
        <button onClick={onRight} disabled={!canMoveRight}
          className="text-[11px] text-slate-500 hover:text-slate-800 disabled:opacity-25 disabled:cursor-not-allowed flex items-center gap-1">
          Avanzar <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};
